import { Box } from "@mui/material";
import React, { useCallback } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";

import { TimeRange } from "helpers/dateRangeHelpers";
import BloodPressureTooltip from "./BloodPressureTooltip";

interface BloodPressureScatterChartProps {
  chartData: Array<{
    date: string;
    systolic: number;
    diastolic: number;
    isCurrent: boolean;
  }>;
  selectedTimeRange: TimeRange;
  colors: { primary: { main: string } };
}

/**
 * Blood pressure scatter chart component
 */
const BloodPressureScatterChart: React.FC<BloodPressureScatterChartProps> = ({
  chartData,
  selectedTimeRange,
  colors,
}) => {
  const tooltipContent = useCallback(
    (props: {
      active?: boolean;
      payload?: Array<{ payload: { systolic: number; diastolic: number } }>;
    }) => <BloodPressureTooltip {...props} colors={colors} />,
    [colors],
  );

  return (
    <Box sx={{ flex: 1, width: "100%", mt: 1, mb: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart data={chartData}>
          <CartesianGrid stroke="#E8E8E9" strokeWidth={1} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            interval={selectedTimeRange === "1M" ? 6 : 0}
          />
          <YAxis tickLine={false} axisLine={false} domain={[50, 200]} />
          <Tooltip content={tooltipContent} />

          {/* Systolic data points (blue circles) */}
          <Scatter dataKey="systolic" fill={colors.primary.main}>
            {chartData.map((entry) => (
              <Cell
                key={`systolic-${entry.date}-${entry.systolic}`}
                fill={colors.primary.main}
                r={entry.isCurrent ? 6 : 4}
              />
            ))}
          </Scatter>

          {/* Diastolic data points (white diamonds) */}
          <Scatter
            dataKey="diastolic"
            fill="#FFFFFF"
            stroke="#000000"
            strokeWidth={1}
          >
            {chartData.map((entry) => (
              <Cell
                key={`diastolic-${entry.date}-${entry.diastolic}`}
                fill="#FFFFFF"
                stroke="#000000"
                strokeWidth={1}
                r={entry.isCurrent ? 6 : 4}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default BloodPressureScatterChart;
